import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowLeft, Lock } from "lucide-react";
import Logo from "@/components/Logo";
import WhatsAppIcon, { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { waLink, WA_RESUME_MSG } from "@/utils/whatsapp";
import { Reveal, EASE } from "@/components/Reveal";
import { JOBS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Vacancies() {
  const [jobs, setJobs] = useState(JOBS);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/jobs`)
      .then((r) => {
        if (Array.isArray(r.data) && r.data.length) setJobs(r.data);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="vacancies-page">
      <header className="border-b border-white/10">
        <div className="h-[3px] w-full bg-gradient-to-r from-[#1e5aa8] via-[#3f9be0] to-[#f06423]" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-[84px] flex items-center justify-between">
          <Link to="/" data-testid="vacancies-logo" aria-label="HR99 Global Services — home">
            <Logo />
          </Link>
          <div className="flex items-center gap-5">
            <Link
              to="/admin/login"
              className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-primary transition-colors"
              data-testid="vacancies-team-login"
            >
              <Lock className="w-3.5 h-3.5" /> Team Login
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-primary transition-colors"
              data-testid="vacancies-back"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-xs uppercase tracking-[0.3em] text-primary font-semibold mb-4"
          data-testid="vacancies-overline"
        >
          HR99 Global Services · Careers
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight text-white"
          data-testid="vacancies-heading"
        >
          Open Vacancies
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
          className="mt-6 text-zinc-400 max-w-2xl leading-relaxed"
          data-testid="vacancies-subtext"
        >
          Every role below is a live mandate from our client companies. Apply
          instantly on WhatsApp — your resume goes straight to our recruitment
          team.
        </motion.p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="vacancies-list">
          {jobs.map((j, i) => (
            <Reveal key={j.id || j.title} delay={(i % 2) * 0.08}>
              <div
                className="group h-full bg-zinc-900 border border-zinc-800 hover:border-primary/50 p-8 transition-colors duration-500"
                data-testid={`vacancy-card-${i}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h2 className="font-heading text-2xl font-medium text-white group-hover:text-primary transition-colors duration-300">
                      {j.title}
                    </h2>
                    <p className="mt-2 flex items-center gap-2 text-sm text-zinc-500">
                      <MapPin className="w-3.5 h-3.5" /> {j.location} · {j.type}
                    </p>
                    {j.experience && (
                      <span
                        className="mt-3 inline-block text-xs px-3 py-1 border border-primary/40 text-primary"
                        data-testid={`vacancy-exp-${i}`}
                      >
                        {j.experience}
                      </span>
                    )}
                  </div>
                  <span className="font-heading text-sm text-zinc-600">
                    /{String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                {j.description && (
                  <p className="mt-4 text-sm text-zinc-400 leading-relaxed" data-testid={`vacancy-desc-${i}`}>
                    {j.description}
                  </p>
                )}
                <a
                  href={waLink(
                    `Hi HR99 Global! I want to apply for the role of ${j.title} (${j.location}). Sharing my resume here.`
                  )}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-3 text-sm font-semibold hover:brightness-110 transition"
                  data-testid={`vacancy-apply-${i}`}
                >
                  <WhatsAppIcon className="w-4 h-4" /> Apply on WhatsApp
                </a>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16 border border-primary/30 bg-primary/5 p-8 md:p-10 text-center">
          <p className="font-heading text-2xl font-medium text-white" data-testid="vacancies-fallback-heading">
            Don't see your role?
          </p>
          <p className="mt-2 text-sm text-zinc-400 max-w-lg mx-auto">
            New mandates open every week across 400+ cities. Share your resume
            and we'll call you the moment something fits.
          </p>
          <a
            href={waLink(WA_RESUME_MSG)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-3 bg-[#25D366] text-white px-6 py-3.5 text-sm font-semibold hover:brightness-110 transition"
            data-testid="vacancies-general-apply"
          >
            <WhatsAppIcon className="w-5 h-5" /> Share Resume on WhatsApp
          </a>
        </Reveal>
      </main>

      <WhatsAppFloat />
    </div>
  );
}
